/**
 * The Applications section of the Contentful product pages: the entry's `applicationsBlurb` beside
 * a list of its `applications`, each with the same icon. From `lg` up the list is two columns;
 * below `lg` it is one column.
 *
 * Rendered by `components/Contentful/Product.tsx` and `SubProduct.tsx` as the first `AllSections`
 * section, with `staticContent` from `content/ryerson/products/productStaticContent.ts` or
 * `subCategoryStaticContent.ts` → `ApplicationsStaticContent`. The entry's `applications` list
 * must be present: it is mapped without a guard.
 */
import React from "react";
import { Icon, IconList } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Column, Grid, Row } from "@ryerson/frontend.layout";

/** One language's fixed copy for the section. */
export type StaticApplicationsContent = {
	/** Section heading; also the source of the section's anchor id (`AllSections`). */
	title: string;
	/** Link text in the page's `SubNavigation` bar. */
	navLabel: string;
	/** The `@ryerson/frontend.assets` icon shown beside every application. */
	icon: keyof IconList;
};

/** The section's fields from the Contentful entry. */
export type DynamicApplicationsContent = {
	/** The entry's `applicationsBlurb`. */
	description: string;
	/** The entry's `applications`, one label each. */
	applications: string[];
};

/** Props of the default export. */
export type ApplicationProps = {
	staticContent: StaticApplicationsContent;
	dynamicContent: DynamicApplicationsContent;
};

type ApplicationsCardType = {
	icon: keyof IconList;
	applications: string[];
};

const Title = styled(Typography)`
	font-weight: 500;
	margin-bottom: 40px;
`;
const Divider = styled.div`
	width: 100%;
	opacity: 0.1;
	margin-bottom: 69px;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.header};
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 0px;
			}
		`;
	}}
`;
const AppSection = styled(Column)`
	padding-bottom: 0;
`;
const App = styled(Flex)`
	margin-bottom: 10px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				padding-top: 19px;
			}
		`;
	}}
`;
const AppLabel = styled(FlexItem)`
	padding-left: 20px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				padding-bottom: 10px;
			}
		`;
	}}
`;

/**
 * The application list. From `lg` up (two columns), a divider follows every item except the last
 * two; below `lg`, every item except the last.
 */
const ApplicationsCard: React.FC<ApplicationsCardType> = ({ applications, icon }) => {
	const { theme } = useTheme();
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<Grid>
					<Row>
						{applications.map((application: string, index: number) => (
							<AppSection lg={6} gutter={10} key={index}>
								<App theme={theme}>
									<Icon
										icon={icon}
										size="xl"
										css={css`
											margin-bottom: 10px;
										`}
									/>
									<AppLabel alignSelf="center" theme={theme}>
										<Typography
											size="sm"
											weight="bold"
											color={theme.colors.primary.secondaryBrand}
										>
											{application}
										</Typography>
									</AppLabel>
								</App>
								{index < applications.length - 2 ? (
									<Divider
										theme={theme}
										css={css`
											@media only screen and (min-width: ${theme.breakpoints
													.lg}) {
												margin-bottom: 0;
											}
										`}
									/>
								) : (
									<></>
								)}
							</AppSection>
						))}
					</Row>
				</Grid>
			</Media>
			<Media lessThan="lg">
				{applications.map((application: string, index: number) => (
					<AppSection lg={6} key={index}>
						<App theme={theme}>
							<FlexItem>
								<Icon
									icon={icon}
									size="xl"
									css={css`
										margin-bottom: 10px;
									`}
								/>
							</FlexItem>
							<AppLabel alignSelf="center" theme={theme}>
								<Typography
									size="sm"
									weight="bold"
									color={theme.colors.primary.secondaryBrand}
								>
									{application}
								</Typography>
							</AppLabel>
						</App>
						{index < applications.length - 1 ? (
							<Divider
								theme={theme}
								css={css`
									@media only screen and (min-width: ${theme.breakpoints.lg}) {
										margin-bottom: 0;
									}
								`}
							/>
						) : (
							<></>
						)}
					</AppSection>
				))}
			</Media>
		</>
	);
};

/**
 * The Applications section. From `lg` up it prints its own heading; below `lg` it doesn't, and
 * the `AllSections` accordion tab carries the section title.
 */
const ApplicationsComponent: React.FC<ApplicationProps> = ({ staticContent, dynamicContent }) => {
	const { theme } = useTheme();
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="121px" type="secondary">
					<Title variant="h1">{staticContent.title}</Title>
					<Divider theme={theme} />
					<Grid>
						<Row>
							<Column lg={5}>
								<Typography variant="p">{dynamicContent.description}</Typography>
							</Column>
							<Column lg={7}>
								<div
									css={css`
										padding-left: 100px;
									`}
								>
									<ApplicationsCard
										applications={dynamicContent.applications}
										icon={staticContent.icon}
									/>
								</div>
							</Column>
						</Row>
					</Grid>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection vPadding="15px" hPadding="0">
					<Typography variant="p" size="sm">
						{dynamicContent.description}
					</Typography>
					<ApplicationsCard
						applications={dynamicContent.applications}
						icon={staticContent.icon}
					/>
				</ContentSection>
			</Media>
		</>
	);
};
export default ApplicationsComponent;
